import type { AsyncOperationNameEnum, AsyncOperationResult } from 'js/src/client';
import { ApiService, AsyncOperationStatus } from 'js/src/client';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { ErrorObject } from 'js/src/types';
import {
    isApiError,
    isApiErrorWithProperty,
    MaxRequestsError,
    OperationAbortedError,
} from 'js/src/types';

/* eslint-disable prefer-promise-reject-errors */
const MAX_FAILURES = 3; // number of tries
const POLL_FREQUENCY = 2000; // milliseconds
const MAX_REQUESTS = 300; // ~10 min max tolerance

type PollAsyncOperationResult<T> = T extends AsyncOperationResult['result']
    ? T
    : AsyncOperationResult['result'];

class PollAsyncOperationStatusError extends Error {
    status: number;
    errorThrown: string;
    expectedError: boolean;
    constructor(status: number, errorThrown: string, expectedError: boolean) {
        super(errorThrown);
        this.status = status;
        this.errorThrown = errorThrown;
        this.expectedError = expectedError;
    }
}

class PollAsyncOperation {
    static _poll_task_result<T>(
        async_operation_uuid: string,
        async_operation_name: AsyncOperationNameEnum | undefined,
        resolve: (
            value: PollAsyncOperationResult<T> | PromiseLike<PollAsyncOperationResult<T>>,
        ) => void,
        reject: (reason: ErrorObject) => void,
        abort_controller: AbortController | null,
        max_requests: number,
        fail_count = 0,
        num_requests = 1,
    ) {
        // Stop polling when we receive the abort signal
        // Abort handler has already fulfilled poll promise
        if (abort_controller && abort_controller.signal.aborted) {
            return;
        }

        if (num_requests > max_requests) {
            reject({
                error: new MaxRequestsError(`Request maximum reached for ${async_operation_name}`),
            });
            return;
        }

        ApiService.asyncOperationResultRetrieve({
            path: { async_operation_uuid },
            query: { success_step: async_operation_name },
        })
            .then((response) => {
                switch (response.data.status) {
                    case AsyncOperationStatus.PROCESSING: {
                        window.setTimeout(() => {
                            this._poll_task_result(
                                async_operation_uuid,
                                async_operation_name,
                                resolve,
                                reject,
                                abort_controller,
                                max_requests,
                                fail_count,
                                num_requests + 1,
                            );
                        }, POLL_FREQUENCY);

                        break;
                    }
                    case AsyncOperationStatus.SUCCESS: {
                        resolve(response.data.result as PollAsyncOperationResult<T>);

                        break;
                    }
                    case AsyncOperationStatus.FAILED: {
                        const rejectData = {
                            error: new PollAsyncOperationStatusError(
                                response.response.status,
                                response.data.message ?? 'unknown error',
                                response.data.expected_error ?? false,
                            ),
                        };
                        reject(rejectData);

                        break;
                    }
                    // No default
                }
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                if (
                    isApiErrorWithProperty(error, 'status') &&
                    error.body.status === AsyncOperationStatus.FAILED
                ) {
                    const rejectData = {
                        error: new PollAsyncOperationStatusError(
                            error.status,
                            isApiErrorWithProperty(error, 'message')
                                ? (error.body.message as string)
                                : 'unknown error',
                            isApiErrorWithProperty(error, 'expected_error')
                                ? (error.body.expected_error as boolean)
                                : false,
                        ),
                    };
                    reject(rejectData);
                    return;
                }

                // handle unknown failure
                fail_count++;
                if (fail_count >= MAX_FAILURES) {
                    let rejectData = {
                        error: new PollAsyncOperationStatusError(
                            -1,
                            `Max fail count reached for ${async_operation_name}`,
                            false,
                        ),
                    };
                    if (isApiError(error)) {
                        rejectData = {
                            error: new PollAsyncOperationStatusError(
                                error.status,
                                isApiErrorWithProperty(error, 'message')
                                    ? (error.body.message as string)
                                    : error.status.toString(),
                                false,
                            ),
                        };
                    }
                    reject(rejectData);
                } else {
                    window.setTimeout(() => {
                        this._poll_task_result(
                            async_operation_uuid,
                            async_operation_name,
                            resolve,
                            reject,
                            abort_controller,
                            max_requests,
                            fail_count,
                            num_requests + 1,
                        );
                    }, POLL_FREQUENCY);
                }
            });
    }

    /**
     * Polls the AsyncOperation endpoint for the status of the user upload. Returns a promise which resolves with the result of the
     * async operation (or rejects if timesout or fails).
     * @param async_operation_uuid: a unique uuid of the user upload
     * @param async_operation_name: optional unique enum of the async operation name
     * @param abort_controller: an AbortController instance that will cancel polling when it receives an abort signal
     */
    static poll_for_result<ResponseType>(
        async_operation_uuid: string,
        async_operation_name?: AsyncOperationNameEnum,
        abort_controller: AbortController | null = null,
        poll_once = false,
    ): Promise<PollAsyncOperationResult<ResponseType>> {
        return new Promise((resolve, reject) => {
            if (abort_controller && abort_controller.signal.aborted) {
                reject({
                    error: new OperationAbortedError(abort_controller.signal.reason),
                });
                return;
            }

            abort_controller &&
                abort_controller.signal.addEventListener(
                    'abort',
                    () => {
                        reject({
                            error: new OperationAbortedError(abort_controller.signal.reason),
                        });
                    },
                    { once: true },
                );

            const max_requests = poll_once ? 1 : MAX_REQUESTS;
            PollAsyncOperation._poll_task_result<ResponseType>(
                async_operation_uuid,
                async_operation_name,
                resolve,
                reject,
                abort_controller,
                max_requests,
            );
        });
    }
}

export const isPollAsyncOperationStatusError = (error: unknown) => {
    return error instanceof PollAsyncOperationStatusError;
};

export default PollAsyncOperation;
